import { AlertCircle, ImageIcon, Trash2, Upload } from "lucide-react";
import { type DragEvent, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { formatBytes, useFileUpload } from "@/hooks/use-file-upload";
import { cn } from "@/lib/utils";

interface LogoUploadProps {
  currentLogoUrl?: string | null;
  onLogoChange: (file: File | "") => void;
  error?: string;
  label?: string;
  className?: string;
}

interface DropzonePreviewProps {
  displayUrl: string | null;
  isDragging: boolean;
  isFocused: boolean;
  isFlashing: boolean;
  onPaste: (e: React.ClipboardEvent) => void;
  onRemove: () => void;
  onFocus: () => void;
  onBlur: () => void;
  onDragEnter: (e: DragEvent<HTMLElement>) => void;
  onDragLeave: (e: DragEvent<HTMLElement>) => void;
  onDragOver: (e: DragEvent<HTMLElement>) => void;
  onDrop: (e: DragEvent<HTMLElement>) => void;
}

const DropzonePreview = ({
  displayUrl,
  isDragging,
  isFocused,
  isFlashing,
  onPaste,
  onRemove,
  onFocus,
  onBlur,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
}: DropzonePreviewProps) => {
  const showUploadIcon = !displayUrl && (isFocused || isDragging);

  return (
    <div className="relative flex-shrink-0">
      {/* biome-ignore lint/a11y/useSemanticElements: dropzone needs div for complex layout */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Logo dropzone - focus and paste image"
        className={cn(
          "size-32 rounded-lg border-2 transition-all duration-200 overflow-hidden",
          "outline-none",
          (isFocused || isDragging) &&
            "ring-2 ring-ring ring-offset-2 border-primary bg-primary/5",
          !isFocused &&
            !isDragging &&
            !displayUrl &&
            "border-dashed border-muted-foreground/25",
          !isFocused &&
            !isDragging &&
            displayUrl &&
            "border-solid border-border",
        )}
        onPaste={onPaste}
        onFocus={onFocus}
        onBlur={onBlur}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        {displayUrl ? (
          <img
            src={displayUrl}
            alt="Logo preview"
            className="size-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            {showUploadIcon ? (
              <Upload
                className={cn("size-8 mb-2", isFocused && "text-primary")}
              />
            ) : (
              <ImageIcon className="size-8 mb-2 opacity-50" />
            )}
            <span className="text-xs text-center px-2">
              {isFocused ? "Ready to paste" : "Drop or paste"}
            </span>
          </div>
        )}

        <div
          className={cn(
            "absolute inset-0 bg-white/50 pointer-events-none transition-opacity duration-200",
            isFlashing ? "opacity-100" : "opacity-0",
          )}
        />
      </div>
      {displayUrl && (
        <Button
          type="button"
          size="icon"
          variant="destructive"
          onClick={onRemove}
          className="absolute -top-2 -right-2 size-6 rounded-full"
          aria-label="Remove logo"
        >
          <Trash2 className="size-3" />
        </Button>
      )}
    </div>
  );
};

const ACCEPTED_TYPES =
  "image/png,image/jpeg,image/jpg,image/webp,image/svg+xml";
const MAX_SIZE = 5 * 1024 * 1024;

export default function LogoUpload({
  currentLogoUrl,
  onLogoChange,
  error,
  label = "Logo",
  className,
}: LogoUploadProps) {
  const [cleared, setCleared] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);

  const [
    { files, isDragging, errors: fileErrors },
    {
      addFiles,
      clearFiles,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      getInputProps,
    },
  ] = useFileUpload({
    maxSize: MAX_SIZE,
    accept: ACCEPTED_TYPES,
    multiple: false,
    onFilesChange: (uploadedFiles) => {
      const file =
        uploadedFiles[0]?.file instanceof File ? uploadedFiles[0].file : null;
      if (file) {
        setCleared(false);
        onLogoChange(file);
      }
    },
  });

  const filePreview = files[0]?.preview ?? null;
  const displayUrl = filePreview || (cleared ? null : currentLogoUrl) || null;
  const combinedError = error || fileErrors[0];

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        setIsFlashing(true);
        setTimeout(() => setIsFlashing(false), 200);

        const file = item.getAsFile();
        if (file) {
          const extension = file.type.split("/")[1] || "png";
          const namedFile = new File([file], `pasted-image.${extension}`, {
            type: file.type,
          });
          addFiles([namedFile]);
        }
        break;
      }
    }
  };

  const handleRemove = () => {
    clearFiles();
    setCleared(true);
    onLogoChange("");
  };

  return (
    <div className={className}>
      {label && (
        <span className="block text-sm font-medium text-foreground mb-2">
          {label}
        </span>
      )}

      <div className="flex gap-4">
        <DropzonePreview
          displayUrl={displayUrl}
          isDragging={isDragging}
          isFocused={isFocused}
          isFlashing={isFlashing}
          onPaste={handlePaste}
          onRemove={handleRemove}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        />

        <div className="flex flex-col justify-center gap-3">
          <div className="text-sm text-muted-foreground">
            <p>PNG, JPG, WebP or SVG</p>
            <p>Maximum file size: {formatBytes(MAX_SIZE)}</p>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openFileDialog}
          >
            <Upload className="size-4 mr-2" />
            Browse Files
          </Button>
        </div>
      </div>

      {combinedError && (
        <Alert variant="destructive" className="mt-2">
          <AlertCircle className="size-4" />
          <AlertDescription>{combinedError}</AlertDescription>
        </Alert>
      )}

      <input {...getInputProps()} className="hidden" />
    </div>
  );
}
